import React, { Suspense, useContext } from 'react';
import { Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { Container } from 'reactstrap';
import { AppContext } from '../../AppContext';

import {
  AppAside,
  AppFooter,
  AppHeader,
  AppSidebar
} from '../../components/coreui';
// sidebar nav config
import navigation from '../../_nav';
// routes config
import routes from '../../routes';
import DefaultNav from './DefaultNav';
import DefaultBreadcrumb from './DefaultBreadcrumb';
import DefaultAside from './DefaultAside';
import DefaultFooter from './DefaultFooter';
import DefaultHeader from './DefaultHeader';
import ThrottledState from '../../views/Pages/ThrottledState/ThrottledState';
import { Page404Admin } from '../../views/Pages/Page404';
import Loader from '../../components/Loader/Loader';
import TopBanner from '../../components/TopBanner';
import {
  getAuthorizationLevel,
  isAuthorized
} from '../../helpers/authorization';

import { sendFeedback } from '../../actions/feedback';
import { clearThrottled } from '../../actions/app';

const DefaultLayout = props => {
  const {
    impersonate,
    reseller,
    dispatch,
    username,
    group_permissions,
    sendFeedback,
    isThrottled,
    retryAfter,
    throttledPath,
    clearThrottled,
    ...rest
  } = props;
  const { translate, activeLanguage, setActiveLanguage } =
    useContext(AppContext);
  const authorizationLevel = getAuthorizationLevel(impersonate, reseller);
  const { history } = props;

  // Filter menu items based on authorization level
  const filteredNav = { items: [] };

  filteredNav.items = navigation.items.filter(item => {
    if (item.authKey === 'statistics' || item.authKey === 'live_statistic')
      return group_permissions.statistic;

    return isAuthorized(item, authorizationLevel);
  });

  // Filter app routes based on authorization level
  const filteredRoutes = routes.filter(route => {
    if (route.authKey === 'statistics' || route.authKey === 'live_statistic')
      return group_permissions.statistic;

    return isAuthorized(route, authorizationLevel);
  });

  const bannerContent = () => {
    return (
      <div>
        <h4>{translate('banner_title')}</h4>
        <p>{translate('banner_subtitle')}</p>
        <div className="banner-buttons">
          <button
            className="btn btn-secondary"
            onClick={() => history.push('/features/summaries')}
          >
            <i className="fa fa-info-circle mr-2" />
            {translate('banner_button_1')}
          </button>
          <button className="btn btn-orange">
            <i className="fa fa-rocket mr-2" />
            {translate('banner_button_2')}
          </button>
        </div>
      </div>
    );
  };

  const year = new Date().getFullYear();
  const version = process.env.REACT_APP_VERSION;
  const bannerVersion = process.env.REACT_APP_BANNER_VERSION;
  // const routesToRender = ['/dashboard', '/extensions', '/users/licenses'];
  const routesToRender = [];

  return (
    <div className={`app app-background footer-fixed ${activeLanguage}`}>
      <AppHeader fixed>
        <Suspense fallback={<Loader loaderOffset={0} />}>
          <DefaultHeader
            translate={translate}
            setLanguage={setActiveLanguage}
            activeLanguage={activeLanguage}
            username={username}
          />
        </Suspense>
      </AppHeader>
      <div className="breadcrumb-class">
        <DefaultBreadcrumb appRoutes={filteredRoutes} translate={translate} />
      </div>
      <div className="app-body">
        <AppSidebar fixed display="lg">
          <Suspense fallback={<Loader loaderOffset={0} />}>
            <DefaultNav
              navConfig={filteredNav}
              translate={translate}
              isThrottled={isThrottled}
              throttledPath={throttledPath}
              onRetry={clearThrottled}
              {...rest}
            />
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <a href="https://telsmart.eu" className="text-white">
                &copy; {year} TelSmart
              </a>
              <span className="ml-2">v{version}</span>
            </div>
          </Suspense>
        </AppSidebar>
        <main className="main">
          <TopBanner
            routesToRender={routesToRender}
            bannerVersion={bannerVersion}
          >
            {bannerContent()}
          </TopBanner>
          <Container fluid>
            <Suspense fallback={<Loader loaderOffset={0} />}>
              {isThrottled && history.location.pathname === throttledPath ? (
                <ThrottledState
                  retryAfter={retryAfter}
                  onRetry={clearThrottled}
                  onBack={() => {
                    clearThrottled();
                    history.goBack();
                  }}
                  translate={translate}
                />
              ) : (
                <Switch>
                  {filteredRoutes.map((route, idx) => {
                    return route.component ? (
                      <Route
                        key={idx}
                        path={route.path}
                        exact={route.exact}
                        name={route.name}
                        render={props => (
                          <route.component
                            authKey={route.authKey}
                            authorizationLevel={authorizationLevel}
                            {...props}
                          />
                        )}
                      />
                    ) : null;
                  })}
                  <Route component={Page404Admin} />
                </Switch>
              )}
            </Suspense>
          </Container>
        </main>
        <AppAside fixed hidden>
          <Suspense fallback={<Loader loaderOffset={0} />}>
            <DefaultAside />
          </Suspense>
        </AppAside>
      </div>
      <AppFooter>
        <Suspense fallback={<Loader loaderOffset={0} />}>
          <DefaultFooter
            username={username}
            sendFeedback={sendFeedback}
            translate={translate}
          />
        </Suspense>
      </AppFooter>
    </div>
  );
};

const mapStateToProps = state => ({
  username: state.profile.profileData.username,
  impersonate: state.profile.profileData.can_impersonate,
  reseller: state.profile.profileData.reseller,
  group_permissions: state.profile.profileData?.group_permissions || {
    mondago: false,
    statistic: false
  },
  isThrottled: state.app.isThrottled,
  retryAfter: state.app.retryAfter,
  throttledPath: state.app.throttledPath
});

const mapDispatchToProps = dispatch => ({
  sendFeedback: feedback => dispatch(sendFeedback(feedback)),
  clearThrottled: () => dispatch(clearThrottled())
});

export default connect(mapStateToProps, mapDispatchToProps)(DefaultLayout);
